import { useState } from 'react';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  IconButton,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReactNode } from 'react';

type ExpandableMenuItemProps = {
  primaryText: string;
  leftIcon: ReactNode;
  children: ReactNode;
  to: string; // Navigation target for the main button
  defaultExpanded?: boolean;
};

export const ExpandableMenuItem = ({
  primaryText,
  leftIcon,
  children,
  to,
  defaultExpanded = false,
}: ExpandableMenuItemProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = location.pathname === to;

  const handleMainClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(to);
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  return (
    <>
      <ListItem disablePadding>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
          }}
        >
          <ListItemButton
            onClick={handleMainClick}
            selected={isActive}
            sx={{
              flex: 1,
              paddingLeft: '16px',
              paddingRight: 0,
              '&:hover': {
                backgroundColor: 'transparent',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(25, 118, 210, 0.16)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.24)',
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 40,
                color: 'inherit',
              }}
            >
              {leftIcon}
            </ListItemIcon>
            <ListItemText primary={primaryText} />
          </ListItemButton>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ height: '24px', alignSelf: 'center' }}
          />
          <IconButton
            onClick={handleExpandClick}
            size="small"
            sx={{
              padding: '8px',
              marginRight: '4px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <ExpandMoreIcon
              sx={{
                transform: expanded
                  ? 'rotate(180deg)'
                  : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          </IconButton>
        </Box>
      </ListItem>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box>{children}</Box>
      </Collapse>
    </>
  );
};
